// Message types for chat
export enum Role {
  USER = 'user',
  ASSISTANT = 'assistant'
}

export interface Message {
  id: string;
  role: Role;
  text: string;
  timestamp: number;
  places?: Place[];
  /** When the agent returns multiple route options, render carousels in chat. */
  routeSuggestions?: RouteSuggestion[];
}

// Location types
export interface Coordinates {
  latitude: number;
  longitude: number;
}

export interface Location {
  lat: number;
  lng: number;
}

// Opening hours from Google Places (optional, fetched client-side)
export interface OpeningHours {
  open_now?: boolean;
  weekday_text?: string[];
}

// Place types
export interface Place {
  place_id: string;
  name: string;
  formatted_address?: string;
  location: Location;
  types?: string[];
  rating?: number;
  user_ratings_total?: number;
  photos?: PlacePhoto[];
  /** First matching user preference / query tag (legacy single field). */
  found_for_preference?: string;
  /** Preferences this place was found for (from agent); first item is the primary query tag. */
  found_for_preferences?: string[];
  extra_time?: number;
  opening_hours?: OpeningHours;
  marker_kind?: 'stop' | 'ranked';
}

export interface PlacePhoto {
  photo_reference: string;
  width: number;
  height: number;
}

// Route types
export interface RouteRequest {
  start: string;
  end: string;
  mode?: TravelMode;
  preferences?: string;
  radius?: number;
  max_time?: number;
  alternative?: boolean;
}

export interface RouteResponse {
  polyline?: string;
  legs?: RouteLeg[];
  suggestions?: RouteSuggestion[];
  reasoning?: string;
  error?: string;
}

export interface RouteLeg {
  distance: { text: string; value: number };
  duration: { text: string; value: number };
  start_address: string;
  end_address: string;
  start_location: Location;
  end_location: Location;
  steps: RouteStep[];
}

export interface RouteStep {
  distance: { text: string; value: number };
  duration: { text: string; value: number };
  start_location: Location;
  end_location: Location;
  html_instructions: string;
  travel_mode: string;
  polyline: { points: string };
}

export interface RouteSuggestion {
  name?: string;
  stops?: Place[];
  suggested_stops?: { lat: number; lng: number; name?: string; place_id?: string }[];
  num_stops?: number;
  total_time: number;
  total_distance?: number;
  polyline: string;
  legs: RouteLeg[];
}

// Saved journey (user can save and load from Settings)
export interface SavedJourney {
  id: string;
  name: string;
  savedAt: number;
  startAddress: string;
  endAddress: string;
  startCoords: Location;
  endCoords: Location;
  places: Place[];
  routePolyline?: string;
  routeSuggestions?: RouteSuggestion[];
}

// Chat types
export interface ChatRequest {
  message: string;
  language?: string;
  session_id?: string;
  start?: string;
  end?: string;
  current_preferences?: string;
  travel_mode?: TravelMode;
  radius?: number; // in km
  max_time?: number; // in minutes
  number_of_people?: number;
}

export interface ChatResponse {
  preferences?: string[];
  specific_places?: string[];
  response?: string;
  user_display_language?: string;
  session_id?: string;
  radius?: number;
  max_travel_time?: number;
  error?: string;
  places?: Place[];
  ranked_places?: Place[];
  // Route suggestions (when agent generates routes)
  suggestions?: RouteSuggestion[];
  reasoning?: string;
}

// Travel mode
export type TravelMode = 'DRIVING' | 'WALKING' | 'BICYCLING' | 'TRANSIT';

/** Preset row from ``GET /api/config`` → ``preference_quick_picks`` (server source of truth). */
export interface PreferenceQuickPick {
  label: string;
  icon: string;
  preference: string;
}

// Navigation tab type
export interface NavTab {
  id: string;
  label: string;
  icon: string;
}
